import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Languages, ChevronDown, Check } from "lucide-react";
import { useTranslation } from "../i18n";
import { T } from "../tokens";

export default function LanguageSwitcher() {
  const { locale, setLocale, locales } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = locales.find((l) => l.code === locale);

  return (
    <>
      <style>{`
        .fyz-lang-opt:hover {
          background: ${T.goldDim} !important;
          color: ${T.goldLt} !important;
        }
      `}</style>
      <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
        <button
          onClick={() => setOpen((p) => !p)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            background: open ? T.goldDim : "rgba(28, 23, 16, 0.4)",
            border: `1px solid ${open ? T.goldBdrS : T.goldBdr}`,
            borderRadius: 12,
            color: open ? T.goldLt : T.muted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            cursor: "pointer",
            transition: "all .25s ease",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            if (!open) {
              e.currentTarget.style.borderColor = T.goldBdrS;
              e.currentTarget.style.color = T.goldLt;
              e.currentTarget.style.background = T.goldDim;
            }
          }}
          onMouseLeave={(e) => {
            if (!open) {
              e.currentTarget.style.borderColor = T.goldBdr;
              e.currentTarget.style.color = T.muted;
              e.currentTarget.style.background = "rgba(28, 23, 16, 0.4)";
            }
          }}
        >
          <Languages size={16} strokeWidth={1.5} />
          <span style={{ letterSpacing: "0.02em" }}>{current?.code?.toUpperCase()}</span>
          <ChevronDown 
            size={14} 
            style={{ 
              opacity: 0.7, 
              transform: open ? "rotate(180deg)" : "none", 
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)" 
            }} 
          />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                minWidth: 180,
                background: T.dark3,
                border: `1px solid ${T.goldBdrS}`,
                borderRadius: 14,
                padding: "8px",
                zIndex: 9999,
                boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
                overflow: "hidden",
              }}
            >
              <div style={{ padding: "8px 12px", fontSize: 11, color: T.gold, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Select Language</div>
              {locales.map((l) => (
                <button
                  key={l.code}
                  className="fyz-lang-opt"
                  onClick={() => {
                    setLocale(l.code);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "10px 12px",
                    background: l.code === locale ? T.goldDim : "transparent",
                    border: "none",
                    borderRadius: 8,
                    color: l.code === locale ? T.goldLt : T.muted,
                    fontSize: 13,
                    fontWeight: l.code === locale ? 600 : 400,
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: "pointer",
                    transition: "all .2s",
                    textAlign: "left",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 11, opacity: 0.5, width: 24 }}>{l.code.toUpperCase()}</span>
                    <span>{l.name}</span>
                  </div>
                  {l.code === locale && <Check size={14} strokeWidth={2.5} />}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

